/* eslint-disable no-shadow */
import React from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { getRatingDetails, setRatingChoice, getPickleBallRating, getMatchTypeIndicator } from '../../../utils';
import { getLabelByTypeId } from '../../../utils/types';
import { sportTypeIds } from '../../../utils/eventConstants';
import { MatchFormatTypes } from '../../../utils/postScoreConstants';
import withAuth from '../../hocs/withAuth';

const UtrLabel = (props) => {
    /** force:
     * - ratingType: singles/doubles
     * - utrType: sanctioned/myUtr
     * - typeDots: true (show singles or doubles dots after utr label)
     */

    const { player, sportTypeId = sportTypeIds.TENNIS, isPickleballRating, hideReliableLabelForPkb, isItGlobalSearch, 'data-testid': dataTestId } = props;
    const { ratingType, utrType, hideUtrLabel, typeDots } = props.force;
    const isPickleBallSportType = sportTypeId === sportTypeIds.PICKLEBALL;

    const singlePkbRatings = player.singlespkbrating || player.historicpkbsinglesrating || player.singlesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const doublesPkbRatings = player.doublespkbrating || player.historicpkbdoublesrating || player.doublesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const utrTypes = coreUI?.types?.tms?.utrTypes;

    const rating = getRatingDetails(setRatingChoice({ player, ratingType, utrType }));
    const estimatedRating = 'Estimated';

    const getHisotricRatingLabel = () => {
        function formatDateString(dateString) {
            const months = [
                'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
            ];

            const date = new Date(dateString);
            const year = date.getFullYear().toString().slice(-2); // Extract the last two digits
            const month = months[date.getMonth()];

            return `${month} '${year}`;
        }
        if ((rating.status === 'Unrated' || rating.status === 'Inactive') && rating.utrRange && !isPickleballRating) {
            if (rating.utrRange.minUtr && rating.utrRange.maxUtr) {
                return estimatedRating;
            }
        }
        if (ratingType === MatchFormatTypes.SINGLES && player.historicRatings?.historicSinglesRating && !player.singlesUtr && !isPickleballRating) {
            return formatDateString(player.historicRatings.historicSinglesRatingDate);
        }
        if (ratingType === MatchFormatTypes.DOUBLES && player?.historicRatings?.historicDoublesRating && !player.doublesUtr && !isPickleballRating) {
            return formatDateString(player.historicRatings.historicDoublesRatingDate);
        }
        if (ratingType === MatchFormatTypes.SINGLES && player?.historicPkbSinglesRating && !player.singlesPkbRating && !!isPickleballRating) {
            return formatDateString(player?.historicPkbSinglesRatingDate);
        }
        if (ratingType === MatchFormatTypes.DOUBLES && player?.historicPkbDoublesRating && !player.doublesPkbRating && !!isPickleballRating) {
            return formatDateString(player.historicPkbDoublesRatingDate);
        }
        return rating.utrType;
    };
    const historicRatingsLabel = getHisotricRatingLabel();
    const utrLabel = typeof utrType === 'string'
        ? historicRatingsLabel
        : getLabelByTypeId(utrTypes, utrType) === 'UTR Event'
            ? 'UTR'
            : getLabelByTypeId(utrTypes, utrType);
    const doublesPicklebalLabel = player.doublesPkbRatingDisplay?.reliability || estimatedRating;
    const singlePicklebalLabel = player.singlesPkbRatingDisplay?.reliability || estimatedRating;

    const renderUtrLabel = () => {
        if (hideUtrLabel || hideReliableLabelForPkb) { return ''; }

        const label = ratingType === MatchFormatTypes.SINGLES ? singlePicklebalLabel : doublesPicklebalLabel;

        return (
            <>
                {label}  {!isItGlobalSearch && rating.value && typeDots && !rating.showPBRRating ? <span title={rating.matchType} data-testid={dataTestId && `${dataTestId}.type-dots`}>{getMatchTypeIndicator(rating)}</span> : ''}
            </>
        );
    };

    if (isPickleballRating) {
        if ((ratingType === MatchFormatTypes.SINGLES && !singlePkbRatings) || (ratingType === MatchFormatTypes.DOUBLES && !doublesPkbRatings)) {
            return '';
        }
        return renderUtrLabel();
    }

    if (isPickleBallSportType) {
        return (
            <>
                {getPickleBallRating(player, ratingType).value}
            </>
        );
    }

    return (
        <>
            {((hideUtrLabel || (utrLabel === 'UTR' && isItGlobalSearch))) ? '' : utrLabel} {!isItGlobalSearch && rating.value && typeDots && !rating.showPBRRating ? <span title={rating.matchType} data-testid={dataTestId && `${dataTestId}.type-dots`}>{getMatchTypeIndicator(rating)}</span> : ''}
        </>
    );
};

UtrLabel.propTypes = {
    player: PropTypes.instanceOf(Object).isRequired,
    isPickleballRating: PropTypes.bool,
    sportTypeId: PropTypes.oneOf([1, 2]).isRequired,
    isItGlobalSearch: PropTypes.bool,
    'data-testid': PropTypes.string,
};

UtrLabel.defaultProps = {
    isPickleballRating: false,
    isItGlobalSearch: false,
    'data-testid': null,
};
export default withAuth(UtrLabel);
